// ============================================================
// 📁 آدرس این فایل در پروژه (فایل جدید):
//    src/pages/finance/journal/useTxnList.ts
//
// لیست صفحه‌بندی‌شده‌ی سمت سرور (۵۰ ردیف در هر صفحه) برای همه‌ی تراکنش‌ها
// + تبدیل فیلترهای صفحه به پارامترهای API (هم برای لیست، هم برای گزارش)
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { JournalFilterValues } from "./JournalFilter";

export const PAGE_SIZE = 50;

export function filterToParams(f: JournalFilterValues, extra?: Record<string, any>): Record<string, any> {
  const p: Record<string, any> = {
    keyword: f.search || undefined,
    date_from: f.dateFrom || undefined,
    date_to: f.dateTo || undefined,
    amount_min: f.amountMin || undefined,
    amount_max: f.amountMax || undefined,
    currency: f.currency || undefined,
    counterparty: f.counterparty || undefined,
    account_id: f.account || undefined,
    category: f.category || undefined,
    product_keyword: f.productName || undefined,
    status: f.status || undefined,
  };
  if (extra) Object.keys(extra).forEach(k => { if (extra[k] !== undefined && extra[k] !== "") p[k] = extra[k]; });
  return p;
}

export function describeFilter(
  f: JournalFilterValues,
  opts?: { counterparty?: { value: string; label: string }[]; account?: { value: string; label: string }[]; category?: { value: string; label: string }[]; counterpartyLabel?: string; extra?: string[] }
): string {
  const parts: string[] = [];
  const lbl = (arr: { value: string; label: string }[] | undefined, v: string) => arr?.find(o => o.value === v)?.label || v;
  if (f.search) parts.push(`جستجو: ${f.search}`);
  if (f.counterparty) parts.push(`${opts?.counterpartyLabel || "طرف حساب"}: ${lbl(opts?.counterparty, f.counterparty)}`);
  if (f.productName) parts.push(`جنس: ${f.productName}`);
  if (f.account) parts.push(`حساب: ${lbl(opts?.account, f.account)}`);
  if (f.category) parts.push(`دسته: ${lbl(opts?.category, f.category)}`);
  if (f.currency) parts.push(`ارز: ${f.currency}`);
  if (f.amountMin || f.amountMax) parts.push(`مبلغ: ${f.amountMin || "…"} تا ${f.amountMax || "…"}`);
  (opts?.extra || []).forEach(x => x && parts.push(x));
  return parts.join(" | ");
}

interface Opts {
  fetcher: (p: any) => Promise<any>;
  respKey: string;
  filter: JournalFilterValues;
  statusFilter: string;
  extra?: Record<string, any>;
}

export function useTxnList({ fetcher, respKey, filter, statusFilter, extra }: Opts) {
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [totals, setTotals] = useState<{ currency: string; amount: number }[]>([]);
  const [usdTotal, setUsdTotal] = useState<number | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const extraKey = JSON.stringify(extra || {});
  const fKey = JSON.stringify(filterToParams(filter)) + "|" + statusFilter + "|" + extraKey;

  const reload = useCallback(async () => {
    setLoading(true);
    const params = filterToParams(filter, { ...(extra || {}), status: statusFilter === "all" ? undefined : statusFilter });
    const res = await fetcherRef.current({ ...params, page, page_size: PAGE_SIZE });
    if (res && res.success) {
      setList(res[respKey] || []);
      setTotalPages(res.total_pages || 1);
      setTotal(res.total ?? 0);
      setTotalAll(res.total_all ?? res.total ?? 0);
      setPendingCount(res.pending_count || 0);
      setConfirmedCount(res.confirmed_count ?? res.paid_count ?? 0);
      setTotals(res.totals || []);
      setUsdTotal(res.usd_total ?? null);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fKey, respKey]);

  // با تغییر فیلتر به صفحه‌ی اول برگرد
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fKey]);

  // درخواست با کمی تأخیر (debounce) تا هر ضربه‌ی کلید یک درخواست جدا نسازد
  useEffect(() => {
    const t = setTimeout(() => { reload(); }, 300);
    return () => clearTimeout(t);
  }, [reload]);

  return { list, loading, page, setPage, totalPages, total, totalAll, pendingCount, confirmedCount, totals, usdTotal, reload };
}
